import { RefCallback, memo, useCallback } from 'react'

import {
  TrExpandable,
  Td,
  useCheckStateContext,
  fancyRowExpandedHeaderClassName,
  useFancyRowExpandedStyle,
  FancyRowHeader,
  TrExpandedContent,
} from 'app/common/ui-components/table'

import { Statement } from 'app/dataTypes/cafeteria/compensation/statements'

import { Column } from './columns'
import { Cell } from './cell'
import { getStatusColor } from './statusColors'
import { SubTableInfo } from './subTableInfo'


type Props = {
  columns: Array<Column>
  columnProps: Record<Column, {
    alignLeft?: boolean
    alignRight?: boolean
    alignCenter?: boolean
  }>
  statement: Statement
  index: number
  calcTopShift: (index: number) => number
  trElem: HTMLTableRowElement | null
  updateRowNode: (el: HTMLTableRowElement | null, index: number) => void
  onRemove: (ID: string) => void
  onEdit: (ID: string) => void
  disabled?: boolean
  innerTableHeaderRef: RefCallback<'tr'>
}

export const TableRow = memo(({
  columns,
  columnProps,
  statement,
  index,
  calcTopShift,
  trElem,
  updateRowNode,
  onRemove,
  onEdit,
  disabled,
  innerTableHeaderRef,
}: Props) => {
  const { ID, status } = statement

  const [expandedID, setExpandedID] = useCheckStateContext()
  const expanded = expandedID === ID
  const tinted = Boolean(expandedID && expandedID !== ID)

  const color = getStatusColor(status)
  const fancyStyle = useFancyRowExpandedStyle(color)

  const reference = useCallback((el: HTMLTableRowElement | null) => {
    updateRowNode(el, index)
  }, [index, updateRowNode])

  return (
    <TrExpandable
      classNameExpanded={fancyRowExpandedHeaderClassName}
      style={fancyStyle}
      styleExpanded={fancyStyle}
      reference={reference}
      id={ID}
      expandedContent={
        <TrExpandedContent fancy>
          {({ beforeLoadData, afterLoadData, loading }) => (
            <SubTableInfo
              headerRef={innerTableHeaderRef}
              ID={ID}
              beforeLoadData={beforeLoadData}
              afterLoadData={afterLoadData}
              loading={loading}
            />
          )}
        </TrExpandedContent>
      }
      expanded={expanded}
      onExpand={setExpandedID}
      tinted={tinted}
      columnsAmount={columns.length}
      disabled={disabled}
      interlineBorder
      alignTop
    >
      {columns.map(key => (
        <Td key={key} {...columnProps[key]}>
          <Cell
            columnKey={key}
            statement={statement}
            onRemove={onRemove}
            onEdit={onEdit}
            disabled={disabled}
          />
        </Td>
      ))}

      <FancyRowHeader
        expanded={expanded}
        requestTopShift={calcTopShift}
        rowIndex={index}
        color={color}
        trElem={trElem}
      />
    </TrExpandable>
  )
})
